import type { ReactNode } from "react";
import { ShieldOff } from "lucide-react";

import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import { MobileBackButton } from "@/components/atomic-crm/misc/MobileBackButton";
import type { Fraktion } from "@/components/atomic-crm/misc/fraktionen";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";

import { voltAreas, type VoltRoles } from "../navigation/voltAreas";
import { VoltEmpty } from "../ui/VoltEmpty";
import { useVoltRoles } from "./attendanceApi";
import { attendanceDistrict } from "./attendanceLogic";

/** Page shell: mobile header with back button on the phone, plain container on desktop. */
export const AttendanceFrame = ({
  title,
  back,
  children,
}: {
  title: string;
  back?: string;
  children: ReactNode;
}) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <>
        <MobileHeader>
          {back ? <MobileBackButton to={back} /> : null}
          <div className="flex min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold">{title}</h1>
          </div>
        </MobileHeader>
        <MobileContent>{children}</MobileContent>
      </>
    );
  }
  return <div className="mx-auto w-full max-w-6xl px-4 py-6">{children}</div>;
};

/** Shows the page only to admins of a Fraktion and superadmins; RLS enforces the same. */
export const AttendanceAccess = ({
  children,
}: {
  children: (ctx: { roles: VoltRoles; district: Fraktion }) => ReactNode;
}) => {
  const { roles, ready } = useVoltRoles();
  if (!ready) return <Skeleton className="h-40 w-full" />;
  const district = attendanceDistrict(roles);
  if (!roles || !voltAreas(roles).attendance || !district) {
    return (
      <VoltEmpty
        icon={ShieldOff}
        title="Kein Zugriff"
        description="Die Anwesenheit sehen nur Admins der Fraktion und Superadmins."
      />
    );
  }
  return <>{children({ roles, district })}</>;
};
